import { mergeChartData } from "./chart-data.js";

// Background history work never replaces the active chart or its viewport.
export class HistoryWindowController {
  constructor({ read, load, apply, dateOf, onError = () => {}, now = () => Date.now(), refreshIntervalMs = 30_000 }) {
    Object.assign(this, { read, load, apply, dateOf, onError, now, refreshIntervalMs });
    this.reset();
  }

  reset() {
    this.controller?.abort();
    this.controller = new AbortController();
    this.older = null;
    this.refreshing = null;
    this.olderExhausted = false;
    this.olderEndDate = null;
    this.nextRefreshAt = 0;
    this.olderRetryAt = 0;
    this.initialTailPending = false;
    this.initialExpansion = null;
    this.expanding = null;
  }

  recordInitial(result, { endDate = null, targetCount = 0, includeCurrent = true } = {}) {
    this.olderExhausted = result.exhausted === true;
    this.olderEndDate = result.nextEndDate || null;
    this.initialTailPending = Boolean(endDate);
    if (!endDate && result.data?.length) this.nextRefreshAt = this.now() + this.refreshIntervalMs;
    this.initialExpansion = !result.exhausted && (result.data?.length || 0) < targetCount
      ? { count: targetCount, endDate } : null;
    // When older history is already covered, only the current session remains.
    // Run it through the same post-paint operation without relying on live data.
    if (!endDate && !includeCurrent && !this.initialExpansion) {
      this.initialExpansion = { count: Math.max(10, Math.min(targetCount || 100, 100)), endDate: null, tailOnly: true };
    }
  }

  completeInitial({ live = false } = {}) {
    const state = this.read();
    if (!state?.data?.length) return Promise.resolve();
    const controller = this.controller;
    const fillLatest = live && this.initialTailPending;
    if (fillLatest) this.initialTailPending = false;
    const expansion = this.initialExpansion;
    this.initialExpansion = null;
    let operation = this.expanding || Promise.resolve();
    if (expansion) {
      const { tailOnly, ...request } = expansion;
      operation = this.load({ ...request, signal: controller.signal, bypassCache: false })
        .then(async result => {
          if (!this.current(state.identity, controller)) return;
          if (result.data?.length) this.apply(result);
          if (!tailOnly) {
            this.olderExhausted = result.exhausted === true;
            this.olderEndDate = result.nextEndDate || null;
            // Custom candles can exhaust one bounded source page without
            // exhausting history. Paint each page, then fill older dates in
            // the background without replacing the current viewport. Limit
            // automatic expansion to four pages; panning can follow its cursor.
            let previousEnd = request.endDate || null;
            let pages = 1;
            while (this.current(state.identity, controller) && pages < 4 && !result.exhausted && result.nextEndDate &&
                (this.read()?.data?.length || 0) < request.count) {
              const nextEnd = result.nextEndDate;
              if (previousEnd && nextEnd >= previousEnd) break;
              previousEnd = nextEnd;
              result = await this.load({ count: request.count, endDate: nextEnd,
                signal: controller.signal, bypassCache: false });
              pages++;
              if (!this.current(state.identity, controller)) return;
              if (result.data?.length) this.apply(result);
              this.olderExhausted = result.exhausted === true;
              this.olderEndDate = result.nextEndDate || null;
            }
          }
          if (!expansion.endDate) this.nextRefreshAt = this.now() + this.refreshIntervalMs;
        }).catch(error => {
          if (!this.current(state.identity, controller) || error?.name === "AbortError") return;
          this.onError(error);
          throw error;
        }).finally(() => { if (this.expanding === operation) this.expanding = null; });
      this.expanding = operation;
    }
    return operation.then(() => fillLatest && this.current(state.identity, controller) ? this.refresh() : undefined);
  }

  prefetch() {
    const state = this.read();
    if (!state?.data?.length || this.olderExhausted || this.older || this.expanding || this.now() < this.olderRetryAt ||
        state.viewport.start > Math.max(150, state.viewport.visibleCount * 2)) return this.older;
    const earliest = state.data.timestamps[0];
    const endDate = this.olderEndDate || this.dateOf(earliest);
    if (!endDate) return null;
    const controller = this.controller;
    const operation = this.load({ endDate, signal: controller.signal })
      .then(result => {
        if (!this.current(state.identity, controller)) return;
        const currentFirst = this.read().data?.timestamps[0];
        const nextEndDate = result.nextEndDate && result.nextEndDate < endDate ? result.nextEndDate : null;
        if (!result.data?.length || result.data.timestamps[0] >= currentFirst) {
          // A bounded source window can be empty without exhausting older years.
          this.olderExhausted = result.exhausted === true || !nextEndDate;
          this.olderEndDate = this.olderExhausted ? null : nextEndDate;
          return;
        }
        this.olderExhausted = result.exhausted === true;
        this.olderEndDate = nextEndDate;
        this.apply(result);
      }).catch(error => {
        if (!this.current(state.identity, controller) || error?.name === "AbortError") return;
        this.olderRetryAt = this.now() + 30_000;
        this.onError(error);
      }).finally(() => {
        if (this.older === operation) this.older = null;
      });
    this.older = operation;
    return operation;
  }

  refresh({ force = false } = {}) {
    const state = this.read();
    if (!state?.data?.length || (!force && this.now() < this.nextRefreshAt)) return this.refreshing || Promise.resolve();
    if (this.refreshing) return this.refreshing;
    this.nextRefreshAt = this.now() + this.refreshIntervalMs;
    const controller = this.controller;
    const operation = this.load({ signal: controller.signal, bypassCache: true })
      .then(result => {
        if (this.current(state.identity, controller) && result.data?.length) this.apply(result);
      }).catch(error => {
        if (!this.current(state.identity, controller) || error?.name === "AbortError") return;
        this.onError(error);
        throw error;
      }).finally(() => {
        if (this.refreshing === operation) this.refreshing = null;
      });
    this.refreshing = operation;
    return operation;
  }

  current(identity, controller) {
    return !controller.signal.aborted && this.controller === controller && this.read()?.identity === identity;
  }
}

export function mergeHistoryWindow(existing, incoming, viewport, { transientData = null, pinnedQuoteIndex = null, hoverQuoteIndex = null } = {}) {
  if (!existing?.length) {
    const data = mergeChartData(incoming);
    const visibleCount = Math.min(viewport.visibleCount || 120, data.length);
    return { data, start: Math.max(0, data.length - visibleCount), visibleCount };
  }
  const displayed = transientData || existing;
  const index = Math.max(0, Math.min(displayed.length - 1, Math.floor(viewport.start)));
  const anchor = displayed.timestamps[index];
  const fraction = viewport.start - index;
  const data = mergeChartData(existing, incoming);
  // An older page cannot supersede the live candle. A fresh response covering
  // the authoritative tail can, and then its OHLCV replaces the provisional data.
  const nextTransient = transientData?.length && incoming.timestamps.at(-1) < transientData.timestamps.at(-1)
    ? mergeChartData(transientData, incoming) : null;
  const nextDisplayed = nextTransient || data;
  const wasAtLatest = viewport.start + viewport.visibleCount >= displayed.length - 0.5;
  const remapIndex = value => {
    if (!Number.isInteger(value) || value < 0 || value >= displayed.length) return null;
    const mapped = nextDisplayed.timestamps.indexOf(displayed.timestamps[value]);
    return mapped < 0 ? null : mapped;
  };
  return {
    data,
    transientData: nextTransient,
    pinnedQuoteIndex: remapIndex(pinnedQuoteIndex),
    hoverQuoteIndex: remapIndex(hoverQuoteIndex),
    start: wasAtLatest ? Math.max(0, nextDisplayed.length - viewport.visibleCount)
      : Math.max(0, nextDisplayed.timestamps.indexOf(anchor) + fraction),
    visibleCount: viewport.visibleCount
  };
}
